import React from 'react';
import { ArrowDownAZ, ArrowUpAZ, Search, X } from 'lucide-react';

/**
 * Small, shared controls for filterable server-paged lists.
 *
 * They exist as one set rather than per page so the same gesture means the same
 * thing everywhere — a filter chip always narrows the whole result set, never just
 * the visible page, and the sort arrow always points the way the data is ordered.
 */

// ── Search ───────────────────────────────────────────────────────

export const SearchInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder = 'Search…', className = '' }) => (
  <div className={`relative ${className}`}>
    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 w-full rounded-md border bg-background pl-7 pr-7 text-xs
                 focus:outline-none focus:ring-1 focus:ring-primary"
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
        aria-label="Clear search"
        type="button"
      >
        <X className="h-3 w-3" />
      </button>
    )}
  </div>
);

// ── Segmented filter ─────────────────────────────────────────────

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  /** Optional count shown as a subtle suffix. */
  count?: number;
  /** Tailwind classes applied when this option is active, for status colouring. */
  activeClass?: string;
}

export function FilterChips<T extends string>({
  value, options, onChange, className = '',
}: {
  value: T;
  options: FilterOption<T>[];
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={`inline-flex rounded-md border p-0.5 ${className}`}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors ${
              active
                ? option.activeClass ?? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
            {option.count != null && (
              <span className={`ml-1.5 tabular-nums ${active ? 'opacity-80' : 'opacity-60'}`}>
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Sort ─────────────────────────────────────────────────────────

export interface SortOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Sort key + direction as one control. The direction button is a toggle rather than
 * a second dropdown because reversing an order is the most common adjustment and
 * shouldn't cost two clicks.
 */
export function SortControl<T extends string>({
  sortBy, sortDir, options, onChange, className = '',
}: {
  sortBy: T;
  sortDir: 'asc' | 'desc';
  options: SortOption<T>[];
  onChange: (sortBy: T, sortDir: 'asc' | 'desc') => void;
  className?: string;
}) {
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <select
        value={sortBy}
        onChange={(e) => onChange(e.target.value as T, sortDir)}
        className="h-8 rounded-md border bg-background px-2 text-xs
                   focus:outline-none focus:ring-1 focus:ring-primary"
        aria-label="Sort by"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onChange(sortBy, sortDir === 'asc' ? 'desc' : 'asc')}
        className="flex h-8 w-8 items-center justify-center rounded-md border bg-background
                   text-muted-foreground transition-colors hover:text-foreground"
        title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
        aria-label="Toggle sort direction"
      >
        {sortDir === 'asc'
          ? <ArrowUpAZ className="h-3.5 w-3.5" />
          : <ArrowDownAZ className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

// ── Sortable column header ───────────────────────────────────────

/** Clickable table header that shows and toggles the active sort. */
export function SortableHeader<T extends string>({
  column, label, sortBy, sortDir, onSort, className = '', align = 'left',
}: {
  column: T;
  label: string;
  sortBy: T;
  sortDir: 'asc' | 'desc';
  onSort: (column: T) => void;
  className?: string;
  align?: 'left' | 'right';
}) {
  const active = sortBy === column;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`flex w-full items-center gap-1 select-none transition-colors
                  ${align === 'right' ? 'justify-end' : ''}
                  ${active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'} ${className}`}
      title={`Sort by ${label}`}
    >
      {label}
      {/* Reserve the caret's width even when inactive so headers don't shift on click. */}
      <span className={`text-[9px] leading-none ${active ? 'opacity-100' : 'opacity-0'}`}>
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    </button>
  );
}

// ── Empty state ──────────────────────────────────────────────────

/** Consistent empty state that distinguishes "nothing here yet" from "nothing matched". */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  /** ReactNode, not string: onboarding copy often needs links into the rest of the app. */
  hint?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, title, hint, action }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
    {icon && <div className="text-muted-foreground/50">{icon}</div>}
    <div className="text-sm font-medium">{title}</div>
    {hint && <div className="max-w-sm text-xs text-muted-foreground">{hint}</div>}
    {action && <div className="mt-1">{action}</div>}
  </div>
);
